import { Box, Typography, type SxProps, type Theme } from "@mui/material";
import { forwardRef, type LiHTMLAttributes } from "react";
import type { PlatformUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "components/CustomScrollbars";

export function getPlatformUserPrimaryLabel(u: PlatformUser): string {
  if (u.first_name || u.last_name) {
    return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  }
  return u.username;
}

/** Plain string for Autocomplete getOptionLabel (input + a11y). */
export function formatPlatformUserOptionLabel(u: PlatformUser): string {
  return `${getPlatformUserPrimaryLabel(u)} @${u.username}`;
}

export function PlatformUserLabelBlock({ user }: { user: PlatformUser }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.2,
        textAlign: "left",
        width: "100%",
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        lineHeight={1.3}
        sx={{ display: "block" }}
      >
        {getPlatformUserPrimaryLabel(user)}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: "0.7rem",
          lineHeight: 1.25,
          fontWeight: 400,
        }}
      >
        @{user.username}
      </Typography>
    </Box>
  );
}

export const platformUserAutocompleteComponentsProps: {
  paper: { elevation: number; sx: SxProps<Theme> };
} = {
  paper: {
    elevation: 0,
    sx: (theme: Theme) => ({
      boxShadow: theme.shadows[12],
      border: "1px solid",
      borderColor: "divider",
    }),
  },
};

export function renderPlatformUserAutocompleteOption(
  props: LiHTMLAttributes<HTMLLIElement>,
  option: PlatformUser,
) {
  return (
    <li {...props}>
      <Box sx={{ width: "100%", py: 0.4 }}>
        <PlatformUserLabelBlock user={option} />
      </Box>
    </li>
  );
}

export const PlatformUserAutocompleteListbox = forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(function PlatformUserAutocompleteListbox({ children, ...listboxProps }, ref) {
  return (
    <CustomScrollbars
      ref={ref}
      variant="compact"
      scrollableElement="ul"
      preserveScrollOnGrow
      scrollableProps={listboxProps}
      trackOffsetTop={10}
      trackOffsetBottom={10}
      sx={{ width: "100%", minWidth: 0 }}
    >
      {children}
    </CustomScrollbars>
  );
});
